import React, { useState, useRef } from 'react';
import { Upload, Plus, X, Check, Loader, Film, AlertCircle, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BulkEpisodeUpload = ({ showId, showTitle, authHeader, onUploadComplete }) => {
  const [episodes, setEpisodes] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const addEpisode = () => {
    setEpisodes([
      ...episodes,
      {
        id: Date.now(),
        title: '',
        description: '',
        season: 1,
        episode_number: episodes.length + 1,
        youtube_url: '',
        file: null,
        preview: null
      }
    ]);
  };

  const updateEpisode = (id, field, value) => {
    setEpisodes(episodes.map(ep => 
      ep.id === id ? { ...ep, [field]: value } : ep
    ));
  };

  const handleFileSelect = (id, file) => {
    if (!file) return;
    
    const preview = file.type.startsWith('video/') 
      ? URL.createObjectURL(file) 
      : null;

    updateEpisode(id, 'file', file);
    updateEpisode(id, 'preview', preview);
  };

  const removeEpisode = (id) => {
    const episode = episodes.find(ep => ep.id === id);
    if (episode?.preview) {
      URL.revokeObjectURL(episode.preview);
    }
    setEpisodes(episodes.filter(ep => ep.id !== id));
  };

  const handleBulkUpload = async () => {
    if (episodes.length === 0) {
      setError('Add at least one episode');
      return;
    }

    const validEpisodes = episodes.filter(ep => 
      ep.title && (ep.youtube_url || ep.file)
    );

    if (validEpisodes.length === 0) {
      setError('Each episode needs a title and either a YouTube URL or video file');
      return;
    }

    setUploading(true);
    setError('');
    setResult(null);

    try {
      const formData = new FormData();
      
      const episodesData = validEpisodes.map((ep, index) => ({
        title: ep.title,
        description: ep.description,
        season: ep.season,
        episode_number: ep.episode_number,
        youtube_url: ep.youtube_url,
        has_file: !!ep.file
      }));

      formData.append('episodes_data', JSON.stringify(episodesData));

      // Add files
      validEpisodes.forEach((ep, index) => {
        if (ep.file) {
          formData.append(`files`, ep.file);
        }
      });

      const response = await fetch(
        `${BACKEND_URL}/api/admin/bulk-upload/episodes/${showId}`,
        {
          method: 'POST',
          headers: authHeader.headers,
          body: formData
        }
      );

      const data = await response.json();

      if (response.ok) {
        setResult(data);
        setEpisodes([]);
        if (onUploadComplete) {
          onUploadComplete(data);
        }
      } else {
        setError(data.detail || 'Upload failed');
      }
    } catch (err) {
      setError('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-gray-900 rounded-xl border border-gray-800 p-6" data-testid="bulk-episode-upload">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-green-600/20 p-3 rounded-lg">
          <Upload className="text-green-500" size={24} />
        </div>
        <div>
          <h3 className="text-xl font-bold text-white">Bulk Episode Upload</h3>
          <p className="text-gray-400 text-sm">
            {showTitle ? `Adding episodes to "${showTitle}"` : 'Upload multiple episodes at once'}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {result && (
        <div className="bg-green-500/10 border border-green-500/30 text-green-400 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
          <Check size={18} />
          Uploaded {result.uploaded} episodes ({result.failed} failed)
        </div>
      )}

      {/* Episodes List */}
      <div className="space-y-4 mb-6 max-h-[400px] overflow-y-auto">
        {episodes.map((episode, index) => (
          <div 
            key={episode.id} 
            className="bg-gray-800/50 rounded-lg p-4 border border-gray-700"
          >
            <div className="flex items-start justify-between mb-3">
              <span className="text-white font-medium">Episode {index + 1}</span>
              <button
                onClick={() => removeEpisode(episode.id)}
                className="text-gray-500 hover:text-red-500"
              >
                <Trash2 size={18} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Title */}
              <div>
                <label className="block text-gray-400 text-xs mb-1">Title *</label>
                <input
                  type="text"
                  placeholder="Episode title"
                  value={episode.title}
                  onChange={(e) => updateEpisode(episode.id, 'title', e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm focus:border-red-500 focus:outline-none"
                />
              </div>

              {/* Season & Episode Number */}
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block text-gray-400 text-xs mb-1">Season</label>
                  <input
                    type="number"
                    min="1"
                    value={episode.season}
                    onChange={(e) => updateEpisode(episode.id, 'season', parseInt(e.target.value) || 1)}
                    className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm focus:border-red-500 focus:outline-none"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-gray-400 text-xs mb-1">Episode #</label>
                  <input
                    type="number"
                    min="1"
                    value={episode.episode_number}
                    onChange={(e) => updateEpisode(episode.id, 'episode_number', parseInt(e.target.value) || 1)}
                    className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm focus:border-red-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <label className="block text-gray-400 text-xs mb-1">Description</label>
                <textarea
                  placeholder="Episode description (optional)"
                  value={episode.description}
                  onChange={(e) => updateEpisode(episode.id, 'description', e.target.value)}
                  rows={2}
                  className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm focus:border-red-500 focus:outline-none resize-none"
                />
              </div>

              {/* YouTube URL */}
              <div>
                <label className="block text-gray-400 text-xs mb-1">YouTube URL</label>
                <input
                  type="url"
                  placeholder="https://youtube.com/watch?v=..."
                  value={episode.youtube_url}
                  onChange={(e) => updateEpisode(episode.id, 'youtube_url', e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm focus:border-red-500 focus:outline-none"
                />
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-gray-400 text-xs mb-1">Or Upload Video</label>
                <label className="flex items-center gap-2 bg-gray-900 border border-gray-700 rounded px-3 py-2 cursor-pointer hover:border-gray-600 text-sm">
                  <Film size={16} className="text-gray-500" />
                  <span className="text-gray-400 truncate">
                    {episode.file ? episode.file.name : 'Choose file...'}
                  </span>
                  <input
                    type="file"
                    accept="video/*"
                    className="hidden"
                    onChange={(e) => handleFileSelect(episode.id, e.target.files[0])}
                  />
                </label>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Episode Button */}
      <button
        onClick={addEpisode}
        className="w-full border-2 border-dashed border-gray-700 hover:border-gray-600 rounded-lg py-4 text-gray-400 hover:text-white transition-colors flex items-center justify-center gap-2 mb-4"
      >
        <Plus size={20} />
        Add Episode
      </button>

      {/* Upload Button */}
      <button
        onClick={handleBulkUpload}
        disabled={uploading || episodes.length === 0}
        className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2"
        data-testid="bulk-upload-btn"
      >
        {uploading ? (
          <>
            <Loader size={20} className="animate-spin" />
            Uploading {episodes.length} Episodes...
          </>
        ) : (
          <>
            <Upload size={20} />
            Upload {episodes.length} Episode{episodes.length !== 1 ? 's' : ''}
          </>
        )}
      </button>
    </div>
  );
};

export default BulkEpisodeUpload;
